import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import BackupStatusBadge from '../components/Backup/BackupStatusBadge';
import apiService from '../services/api';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { darkModeClasses } from '../utils/darkModeClasses';
import { Project } from '../types/api';
import Logger from '../utils/logger';

interface BackupItem {
  id: string;
  deployment_id: string;
  backup_name: string;
  status: string;
  backup_type: string;
  size_bytes: number | null;
  s3_path: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  backup_timestamp: string;
}

interface DeploymentWithBackups {
  deployment_id: string;
  deployment_name: string;
  application_type: string;
  backups: BackupItem[];
}

interface EnvironmentWithBackups {
  environment_id: string;
  environment_name: string;
  project_id: string;
  project_name: string;
  deployments: DeploymentWithBackups[];
}

interface AllBackupsResponse {
  environments: EnvironmentWithBackups[];
  total_backups: number;
}

const BackupsPage: React.FC = () => {
  const { t } = (useTranslation as any)('backups');
  const navigate = useNavigate();
  const [data, setData] = useState<AllBackupsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadAllBackups();
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const data = await apiService.getProjects();
      setProjects(data);
    } catch (error) {
      Logger.error('Erreur lors du chargement des projets:', error);
    }
  };

  const loadAllBackups = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllUserBackups(selectedProjectId || undefined);
      setData(response);
    } catch (error: unknown) {
      Logger.error('Error loading backups:', error);
      toast.error(t('management.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const formatSize = (bytes: number | null) => {
    if (bytes === null) return 'N/A';
    const mb = bytes / (1024 * 1024);
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) {
      return `${gb.toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const handleBackupClick = (backup: BackupItem) => {
    navigate(`/deployments/${backup.deployment_id}/backups`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <>
          {/* Header */}
          <div className="mb-8">
            <h1 className={`text-3xl font-bold ${darkModeClasses.textPrimary} mb-2`}>
              <ArchiveBoxIcon className="w-5 h-5 inline mr-1" /> {t('title')}
            </h1>
            <p className={`${darkModeClasses.textSecondary}`}>
              {t('subtitle')}
            </p>
            {data && (
              <p className={`mt-2 text-sm ${darkModeClasses.textMuted}`}>
                {t('total', { count: data.total_backups })}
              </p>
            )}
          </div>

          {/* Project Scope Selector */}
          <div className="mb-6 flex items-center gap-3">
            <label className="text-sm font-medium text-text-secondary">
              {t('scope.label')}
            </label>
            {projects.length > 0 ? (
              <>
                <select
                  value={selectedProjectId || ''}
                  onChange={(e) => setSelectedProjectId(e.target.value || null)}
                  className="block w-64 border border-border-1 rounded-md shadow-card py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="">{t('scope.allProjects')}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
                {selectedProjectId && (
                  <span className="text-xs text-text-tertiary">
                    {t('scope.showsAllMembers')}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-text-tertiary">
                {t('scope.loadingProjects')}
              </span>
            )}
          </div>

          {/* Empty State */}
          {data && data.environments.length === 0 && (
            <div className={`${darkModeClasses.card} p-12 text-center`}>
              <svg
                className="mx-auto h-16 w-16 text-text-quaternary mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
              <h3 className={`text-lg font-medium ${darkModeClasses.textPrimary} mb-2`}>
                {t('empty.title')}
              </h3>
              <p className={`${darkModeClasses.textSecondary}`}>
                {t('empty.subtitle')}
              </p>
            </div>
          )}

          {/* Environments List */}
          <div className="space-y-8">
            {data?.environments.map((environment) => (
              <div key={environment.environment_id} className={`${darkModeClasses.card} p-6`}>
                {/* Environment Header */}
                <div className="mb-6 pb-4 border-b border-border-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className={`text-2xl font-bold ${darkModeClasses.textPrimary}`}>
                        {environment.project_name}
                      </h2>
                      <p className={`text-sm ${darkModeClasses.textSecondary} mt-1`}>
                        {t('environment', { name: environment.environment_name })}
                      </p>
                    </div>
                    <div className={`px-4 py-2 rounded-lg bg-blue-500/12/30`}>
                      <span className={`text-sm font-medium text-blue-400`}>
                        {t('backupCount', { count: environment.deployments.reduce((acc, dep) => acc + dep.backups.length, 0) })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Deployments */}
                <div className="space-y-6">
                  {environment.deployments.map((deployment) => (
                    <div key={deployment.deployment_id} className="space-y-3">
                      {/* Deployment Header */}
                      <div className="flex items-center gap-3">
                        <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary}`}>
                          {deployment.deployment_name}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded bg-surface-3 ${darkModeClasses.textSecondary}`}>
                          {deployment.application_type}
                        </span>
                        <span className={`text-xs ${darkModeClasses.textMuted}`}>
                          {t('backupCount', { count: deployment.backups.length })}
                        </span>
                      </div>

                      {/* Backups - Vertical list */}
                      <div className="bg-surface-2 rounded-lg border border-border-1 overflow-hidden">
                        {deployment.backups.map((backup, idx) => (
                          <div
                            key={backup.id}
                            onClick={() => handleBackupClick(backup)}
                            className={`
                              flex items-center justify-between px-4 py-3 cursor-pointer
                              hover:bg-surface-3 transition-colors
                              ${idx > 0 ? 'border-t border-border-0' : ''}
                            `}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <BackupStatusBadge status={backup.status as any} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {backup.backup_name}
                                </p>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-text-tertiary">
                                  <span>{formatDate(backup.started_at)}</span>
                                  <span className="font-mono">{formatSize(backup.size_bytes)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                backup.backup_type === 'ondemand'
                                  ? 'bg-purple-500/12 text-purple-400'
                                  : 'bg-surface-3 text-text-secondary'
                              }`}>
                                {backup.backup_type === 'ondemand' ? t('manual') : t('auto')}
                              </span>
                              {backup.error_message && (
                                <span className="text-xs text-red-400" title={backup.error_message}>{t('error')}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
    </>
  );
};

export default BackupsPage;
